import { apiClient } from '@/lib/api.ts'

export type KnowledgeSourceType =
  | 'pdf'
  | 'text'
  | 'email'
  | 'sap_offer'
  | 'docx'
  | 'xlsx'
  | 'pptx'
  | 'confluence'
  | 'url'

export type KnowledgeUsageTag = 'artikel' | 'angebote' | 'allgemein'

export const KNOWLEDGE_USAGE_TAG_LABELS: Record<KnowledgeUsageTag, string> = {
  artikel: 'Artikel-Texte',
  angebote: 'Angebote',
  allgemein: 'Allgemein',
}

export type EmbeddingStatus = 'ok' | 'partial' | 'none'

export interface KnowledgeSource {
  id: string
  title: string
  sourceType: KnowledgeSourceType
  chunkCount: number
  /** Number of chunks that have a vector embedding (populated by GET /sources). */
  embeddedChunkCount?: number
  /** Derived from embeddedChunkCount vs chunkCount. 'ok' = all chunks embedded. */
  embeddingStatus?: EmbeddingStatus
  uploadedByUserId: string | null
  usageTags: KnowledgeUsageTag[] | null
  /** null = global (all companies). Set = tenant-specific only. */
  scopeTenantId: string | null
  /** 1 = highest priority, 5 = archive. null = not rated. */
  priority: number | null
  /** Upstream ref — for SAP offers from rag-candidates: "quote:sap:{tenantId}:{docEntry}" */
  externalRef: string | null
  /** Set when the embedding step failed (e.g. missing OpenAI key). Null = no error. */
  embeddingErrorMessage?: string | null
  createdAt: string
}

export interface MergeCandidate {
  sourceIdA: string
  sourceIdB: string
  similarity: number
  titleA: string
  titleB: string
}

export interface Contradiction {
  sourceIdA: string
  sourceIdB: string
  reason: string
}

export interface PrioritySuggestion {
  sourceId: string
  suggestedPriority: number
  reason: string
}

export interface AnalysisResult {
  mergeCandidates: MergeCandidate[]
  contradictions: Contradiction[]
  prioritySuggestions: PrioritySuggestion[]
  sourcesAnalyzed: number
  truncated: boolean
}

export interface KnowledgeChunk {
  id: string
  chunkIndex: number
  content: string
  metadataJson: unknown
}

export interface KnowledgeQueryResult {
  chunkId: string
  sourceId: string
  sourceTitle: string
  sourceType: string
  content: string
  score: number
}

export type ConfluenceSubscriptionType = 'page' | 'space'

export interface ConfluenceSubscription {
  id: string
  tenantId: string
  subscriptionType: ConfluenceSubscriptionType
  pageId: string
  spaceKey: string | null
  spaceId: string | null
  title: string
  sourceId: string | null
  lastVersion: number | null
  pageCount: number
  lastSyncedAt: string | null
  lastErrorMessage: string | null
  createdByUserId: string | null
  createdAt: string
  updatedAt: string
  /** Number of chunks with a vector embedding. */
  embeddedChunkCount?: number
  /** Total number of chunks across all sources for this subscription. */
  totalChunkCount?: number
  /** Derived embedding health status. */
  embeddingStatus?: EmbeddingStatus
  /** For space subscriptions: number of pages with at least 1 embedded chunk. */
  embeddedPageCount?: number
  /** For space subscriptions: total number of ingested pages. */
  totalPageCount?: number
  /** Set when the last embedding attempt failed (e.g. missing OpenAI key). */
  embeddingErrorMessage?: string | null
}

export interface M365MailListItem {
  id: string
  subject: string
  from: { emailAddress: { name: string; address: string } }
  receivedDateTime: string
  isRead: boolean
  bodyPreview: string
}

function parseTagsField(raw: unknown): KnowledgeUsageTag[] {
  if (Array.isArray(raw)) return raw as KnowledgeUsageTag[]
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) as KnowledgeUsageTag[] } catch { return [] }
  }
  return []
}

export async function fetchKnowledgeSources(): Promise<KnowledgeSource[]> {
  const res = await apiClient.get<{ data: KnowledgeSource[] }>('/knowledge-base/sources')
  return res.data.data.map((s) => ({ ...s, usageTags: parseTagsField(s.usageTags) }))
}

export interface IngestResponse {
  source: KnowledgeSource
  /** Set when embedding failed — source is stored but not RAG-searchable. */
  embeddingWarning?: string
}

export async function createKnowledgeSource(params: {
  title: string
  content: string
  usageTags?: KnowledgeUsageTag[]
  scopeTenantId?: string | null
}): Promise<IngestResponse> {
  const res = await apiClient.post<{ data: KnowledgeSource; warning?: string }>('/knowledge-base/sources', params)
  return { source: res.data.data, embeddingWarning: res.data.warning }
}

export async function updateKnowledgeSource(
  id: string,
  patch: { usageTags?: KnowledgeUsageTag[]; scopeTenantId?: string | null; title?: string; priority?: number | null },
): Promise<KnowledgeSource> {
  const res = await apiClient.patch<{ data: KnowledgeSource }>(`/knowledge-base/sources/${id}`, patch)
  return res.data.data
}

export async function updateKnowledgeSourcePriority(id: string, priority: number | null): Promise<void> {
  await apiClient.patch(`/knowledge-base/sources/${id}`, { priority })
}

export async function analyzeKnowledgeBase(): Promise<AnalysisResult> {
  const res = await apiClient.post<{ data: AnalysisResult }>('/knowledge-base/analyze', {})
  return res.data.data
}

/** @deprecated Use updateKnowledgeSource */
export async function updateKnowledgeSourceTags(
  id: string,
  usageTags: KnowledgeUsageTag[],
): Promise<KnowledgeSource> {
  return updateKnowledgeSource(id, { usageTags })
}

/**
 * Uploads a file — the backend auto-detects PDF / DOCX / XLSX.
 */
export async function uploadKnowledgeFile(
  file: File,
  title?: string,
  usageTags?: KnowledgeUsageTag[],
  scopeTenantId?: string | null,
): Promise<IngestResponse> {
  const form = new FormData()
  form.append('file', file)
  if (title) form.append('title', title)
  if (usageTags?.length) form.append('usageTags', JSON.stringify(usageTags))
  if (scopeTenantId) form.append('scopeTenantId', scopeTenantId)
  const res = await apiClient.post<{ data: KnowledgeSource; warning?: string }>('/knowledge-base/upload-file', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return { source: res.data.data, embeddingWarning: res.data.warning }
}

/** @deprecated Use `uploadKnowledgeFile` instead. */
export async function uploadKnowledgePdf(file: File, title?: string): Promise<IngestResponse> {
  return uploadKnowledgeFile(file, title)
}

export async function updateKnowledgeSourceContent(id: string, content: string): Promise<IngestResponse> {
  const res = await apiClient.put<{ data: KnowledgeSource; warning?: string }>(
    `/knowledge-base/sources/${id}/content`,
    { content },
  )
  return { source: res.data.data, embeddingWarning: res.data.warning }
}

export async function deleteKnowledgeSource(id: string): Promise<void> {
  await apiClient.delete(`/knowledge-base/sources/${id}`)
}

export async function fetchKnowledgeChunks(sourceId: string): Promise<KnowledgeChunk[]> {
  const res = await apiClient.get<{ data: KnowledgeChunk[] }>(`/knowledge-base/chunks/${sourceId}`)
  return res.data.data
}

export async function queryKnowledgeBase(query: string, topK = 5): Promise<KnowledgeQueryResult[]> {
  const res = await apiClient.post<{ data: KnowledgeQueryResult[] }>('/knowledge-base/query', { query, topK })
  return res.data.data
}

// ─── Confluence Subscriptions ──────────────────────────────────────────────

export async function listConfluenceSubscriptions(): Promise<ConfluenceSubscription[]> {
  const res = await apiClient.get<{ data: ConfluenceSubscription[] }>('/knowledge-base/confluence/subscriptions')
  return res.data.data
}

export async function addConfluenceSubscription(input: string): Promise<ConfluenceSubscription> {
  const res = await apiClient.post<{ data: ConfluenceSubscription }>(
    '/knowledge-base/confluence/subscriptions',
    { input },
  )
  return res.data.data
}

export async function removeConfluenceSubscription(id: string): Promise<void> {
  await apiClient.delete(`/knowledge-base/confluence/subscriptions/${id}`)
}

export interface ConfluenceSyncResult {
  subscriptionId: string
  status: 'unchanged' | 'updated' | 'error'
  message?: string
  pageId?: string
  newVersion?: number
  spaceKey?: string
  pagesUpdated?: number
  pagesUnchanged?: number
  pagesRemoved?: number
}

export async function syncConfluenceSubscription(id: string): Promise<ConfluenceSyncResult> {
  const res = await apiClient.post<{ data: ConfluenceSyncResult }>(
    `/knowledge-base/confluence/subscriptions/${id}/sync-now`,
    {},
  )
  return res.data.data
}

// ─── M365 Mail Import ──────────────────────────────────────────────────────

export async function listRecentM365Mails(limit = 25): Promise<M365MailListItem[]> {
  const res = await apiClient.get<{ data: M365MailListItem[] }>('/knowledge-base/m365/recent', {
    params: { limit },
  })
  return res.data.data
}

export async function importM365Mail(messageId: string): Promise<KnowledgeSource> {
  const res = await apiClient.post<{ data: KnowledgeSource }>('/knowledge-base/m365/import', { messageId })
  return res.data.data
}

// ─── URL Import ────────────────────────────────────────────────────────────

export async function importKnowledgeUrl(url: string, title?: string): Promise<KnowledgeSource> {
  const res = await apiClient.post<{ data: KnowledgeSource }>('/knowledge-base/url-import', {
    url,
    title,
  })
  return res.data.data
}
